/**
 * Onboarding stories (Figma section 114:13958, design/specs/S-stories.md): three 3-card sets opened
 * as a full-screen overlay. Content and the pure player logic live here, free of Solid, so they are
 * tested with bun (test/stories.test.ts). The player only moves on "Suivant" / "Retour" (decision
 * 14/10: no auto-advance).
 */

export type StorySet = "safety" | "duo" | "zones";

/** Card animation: animated WebP (≤ 200 KB) with a static PNG for reduced motion / no WebP. */
export interface StoryArt {
  webp: string;
  png: string;
  /** Display size in CSS px (the Figma illustration box, aspect kept); shrinks on short phones. */
  width: number;
  height: number;
}

/** Z2 legend row: the icon drawn in a 40 px white disc (or the dashed ring, no disc). */
export interface LegendRow {
  icon: "parking" | "speed" | "warning" | "no-parking" | "zone-limit";
  title: string;
  text: string;
}

export interface StoryCard {
  title: string;
  /** Absent on Z2, which shows the legend instead. */
  body?: string;
  art?: StoryArt;
  legend?: LegendRow[];
}

export interface StorySetDef {
  /** The pill above the title. */
  category: string;
  /** CTA of the last card ("Suivant" on the others). */
  doneLabel: string;
  cards: StoryCard[];
}

const art = (name: string, width: number, height: number): StoryArt => ({
  webp: `/img/stories/${name}.webp`,
  png: `/img/stories/${name}.png`,
  width,
  height,
});

export const STORY_SETS: Record<StorySet, StorySetDef> = {
  safety: {
    category: "Sécurité routière",
    doneLabel: "C'est compris",
    cards: [
      {
        title: "Vérifiez les freins",
        body: "Vérifiez que les deux freins fonctionnent avant de commencer chaque trajet.",
        art: art("s1-brakes", 281, 353),
      },
      {
        title: "Vérifiez les lumières",
        body: "Vérifiez que les lumières avant et arrière sont bien allumées avant de démarrer votre trajet.",
        art: art("s2-lights", 281, 353),
      },
      {
        title: "Attention aux vêtements longs",
        body: "Attention à ne rien coincer dans les roues, notamment si vous portez un manteau long ou une robe.",
        art: art("s3-long-clothes", 326, 410),
      },
    ],
  },
  duo: {
    category: "Mode duo",
    doneLabel: "C'est compris",
    cards: [
      {
        title: "Démarrer à deux",
        body: "Demandez à votre passager de vous aider à prendre de l'élan lorsque vous commencez à pédaler.",
        art: art("s4-duo-start", 330, 330),
      },
      {
        title: "Taille minimale du passager",
        body: "La hauteur recommandée du passager est de 130 cm. Votre passager doit pouvoir atteindre les repose-pieds avec ses deux pieds pour éviter de les coincer dans les roues.",
        art: art("s5-passenger-height", 310, 388),
      },
      {
        title: "Port du casque",
        body: "Le casque est obligatoire pour les passagers de moins de 12 ans. Il est recommandé pour tous.",
        art: art("s6-helmet", 292, 413),
      },
    ],
  },
  zones: {
    category: "Zones et stationnement",
    doneLabel: "C'est parti !",
    cards: [
      {
        title: "Comment se garer ?",
        body: "Trouvez un P bleu sur la carte. Glissez pour terminer, puis suivez les instructions pour mettre fin à votre trajet.",
        art: art("z1-how-to-park", 256, 413),
      },
      {
        title: "Légende de la carte",
        legend: [
          {
            icon: "parking",
            title: "Espace de stationnement",
            text: "Sur ces zones, le stationnement est autorisé.",
          },
          {
            icon: "speed",
            title: "Zone à vitesse réduite",
            text: "La vitesse est limitée à 15 km/h dans cette zone.",
          },
          {
            icon: "warning",
            title: "Zone interdite",
            text: "Il est interdit de circuler dans ces zones, l'assistance électrique s'éteindra et vous risquez une pénalité.",
          },
          {
            icon: "no-parking",
            title: "Stationnement interdit",
            text: "Il est interdit de stationner dans cette zone. Vous risquez une pénalité.",
          },
          {
            icon: "zone-limit",
            title: "Limite de la zone",
            text: "En dehors de la zone d'opération, l'assistance électrique s'éteindra automatiquement.",
          },
        ],
      },
      {
        title: "Et si je me gare ailleurs ?",
        body: "Si vous stationnez votre vélo en dehors d'une zone de stationnement, vous vous exposez à une pénalité.",
        art: art("z3-park-elsewhere", 329, 413),
      },
    ],
  },
};

const clamp = (index: number, count: number) => Math.min(Math.max(0, index), count - 1);

/** CTA label of card `index`: "Suivant", or the set's own label on the last card. */
export function ctaLabel(set: StorySet, index: number): string {
  const def = STORY_SETS[set];
  return clamp(index, def.cards.length) === def.cards.length - 1 ? def.doneLabel : "Suivant";
}

/** "Suivant": the next card, or `done` on the last one (the index stays on it). */
export function nextStep(index: number, count: number): { index: number; done: boolean } {
  const i = clamp(index, count);
  return i >= count - 1 ? { index: i, done: true } : { index: i + 1, done: false };
}

/** "Retour": the previous card, never before the first. */
export function backStep(index: number): number {
  return Math.max(0, index - 1);
}

/** Progress segment `segment` is full on card `index` (done and current), else at 30 %. */
export function segmentFilled(segment: number, index: number): boolean {
  return segment <= index;
}

/** The part of Storage the seen-once flag needs (localStorage in the app). */
type FlagStore = Pick<Storage, "getItem" | "setItem">;

const seenKey = (set: StorySet) => `stories-${set}-seen`;

const storage = (): FlagStore | undefined => {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined; // reading localStorage itself throws when site data is blocked
  }
};

/** Whether the set was already gone through (e.g. S4–S6 only the first time duo is chosen). */
export function storySeen(set: StorySet, store: FlagStore | undefined = storage()): boolean {
  try {
    return store?.getItem(seenKey(set)) === "1";
  } catch {
    return false; // private mode / blocked storage: show it again rather than never
  }
}

export function markStorySeen(set: StorySet, store: FlagStore | undefined = storage()): void {
  try {
    store?.setItem(seenKey(set), "1");
  } catch {
    // Storage unavailable: nothing to remember, the stories just show again next time.
  }
}

/**
 * First ride: the safety tutorial (S1–S3) opens by itself on the start sheet (05b, right after the
 * unlock) while this device never went through it, and only once per ride.
 */
export function autoSafetyTutorial(s: {
  startSheet: boolean;
  seen: boolean;
  shownThisRide: boolean;
}): boolean {
  return s.startSheet && !s.seen && !s.shownThisRide;
}
